import { useState } from 'react';
import { getPosicionInfo } from '../lib/tickerInfo';
import { completeOnboarding, ensureAlpacaAccount } from '../lib/cerebro';
import OraculoVoice from './OraculoVoice';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import PortfolioMark from './PortfolioMark';

// 8 preguntas del onboarding (alineadas con el backend)
const QUESTIONS = [
  {
    id: 'monthlyInvestment',
    title: 'Tu capacidad de inversión',
    question: '¿Cuánto puedes invertir al mes, en dólares?',
    help: 'Los montos están en dólares (USD).',
    options: [
      { value: 'bajo', label: 'Menos de US$100', desc: 'Empezar con poco está bien' },
      { value: 'medio', label: 'US$100 - US$500', desc: 'Inversión constante' },
      { value: 'alto', label: 'US$500 - US$2.000', desc: 'Crecimiento acelerado' },
      { value: 'muy_alto', label: 'Más de US$2.000', desc: 'Inversión seria' },
    ],
  },
  {
    id: 'reactionToDrop',
    title: 'Tu temperamento',
    question: 'Tu inversión baja 25% en una semana. ¿Qué haces?',
    options: [
      { value: 'sell', label: 'Vendo para no perder más', desc: 'Prefiero proteger lo que tengo' },
      { value: 'hold', label: 'No hago nada, espero', desc: 'Tengo paciencia' },
      { value: 'buy_more', label: '¡Compro más! Está en oferta', desc: 'Las caídas son oportunidades' },
    ],
  },
  {
    id: 'timeHorizon',
    title: 'Tu horizonte de tiempo',
    question: '¿Cuándo necesitas esta plata?',
    options: [
      { value: 'short', label: 'Menos de 2 años', desc: 'Corto plazo' },
      { value: 'medium', label: '2 a 5 años', desc: 'Mediano plazo' },
      { value: 'long', label: '5 a 10 años', desc: 'Largo plazo' },
      { value: 'unlimited', label: 'No la necesito', desc: 'Es plata que puedo dejar crecer' },
    ],
  },
  {
    id: 'excitement',
    title: '¿Qué te emociona más?',
    question: '¿Qué tipo de inversión te atrae?',
    options: [
      { value: 'safe_growth', label: 'Crecimiento seguro', desc: 'Dormir tranquilo sabiendo que crece' },
      { value: 'world_changers', label: 'Empresas que cambian el mundo', desc: 'Tesla, NVIDIA, las que innovan' },
      { value: 'passive_income', label: 'Ingresos pasivos', desc: 'Dividendos que me paguen cada mes' },
      { value: 'balance', label: 'Un poco de todo', desc: 'Balance entre crecimiento y seguridad' },
    ],
  },
  {
    id: 'experience',
    title: 'Tu experiencia',
    question: '¿Cuánto sabes de inversiones?',
    options: [
      { value: 'none', label: 'Nada, soy completamente nuevo', desc: 'Quiero aprender desde cero' },
      { value: 'some', label: 'Sé lo básico', desc: 'Sé qué son acciones y ETFs' },
      { value: 'research', label: 'Investigo antes de invertir', desc: 'Leo reportes y analizo' },
    ],
  },
  {
    id: 'sectors',
    title: 'Tus intereses',
    question: '¿Qué sectores te interesan? (puedes elegir varios)',
    multi: true,
    options: [
      { value: 'tech', label: 'Tecnología', desc: 'Apple, NVIDIA, Microsoft' },
      { value: 'health', label: 'Salud', desc: 'Farmacéuticas, biotech' },
      { value: 'energy', label: 'Energía', desc: 'Petróleo, renovables' },
      { value: 'finance', label: 'Finanzas', desc: 'Bancos, fintech' },
      { value: 'all', label: 'Todos me interesan', desc: 'Diversificar en todo' },
    ],
  },
  {
    id: 'destinoGasto',
    title: 'En qué la vas a gastar',
    question: '¿En qué vas a gastar esta plata cuando la necesites?',
    // NO se le pregunta "cuanto quieres en dolares": nadie sabe responder eso.
    // Se le pregunta lo que SI sabe —donde va a gastar— y el sistema deriva la
    // exposicion. Junto con el horizonte decide peso/dolar. Ver data/portafolios.js.
    help: 'Esto define cuánto conviene tener en pesos y cuánto en dólares. No es sobre riesgo: es sobre en qué moneda vas a pagar.',
    options: [
      { value: 'pesos_colombia', label: 'En pesos, aquí en Colombia', desc: 'Casa, carro, estudio, un negocio' },
      { value: 'dolares_exterior', label: 'En dólares o fuera del país', desc: 'Viajes, estudio afuera, un hijo que se va, retiro fuera' },
      { value: 'no_se', label: 'Todavía no sé', desc: 'Está bien: te dejamos en un punto equilibrado' },
    ],
  },
  {
    id: 'concentration',
    title: 'Tu estilo',
    question: '¿Prefieres pocas acciones que conoces bien, o muchas para diversificar?',
    options: [
      { value: 'concentrated', label: 'Pocas pero buenas', desc: '5-10 acciones que entiendo a fondo' },
      { value: 'diversified', label: 'Muchas para diversificar', desc: 'ETFs y muchas acciones diferentes' },
    ],
  },
];

// Portfolios result display
const PORTFOLIOS = {
  guardian: {
    name: 'Guardian',
    icon: '🛡️',
    color: '#2A9D6B',
    desc: 'Protege tu capital mientras crece de forma estable',
    risk: 'Conservador',
    expectedReturn: '6-9% anual',
    composition: [
      { symbol: 'BND', pct: 30, color: '#3B82F6' },
      { symbol: 'SCHD', pct: 25, color: '#10B981' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'GLD', pct: 15, color: '#F59E0B' },
      { symbol: 'VNQ', pct: 10, color: '#8B5CF6' },
    ],
  },
  explorer: {
    name: 'Explorador',
    icon: '🧭',
    color: '#3d8bfd',
    desc: 'Balance entre crecimiento y protección para crecer con confianza',
    risk: 'Moderado',
    expectedReturn: '9-12% anual',
    composition: [
      { symbol: 'VOO', pct: 35, color: '#2563EB' },
      { symbol: 'QQQ', pct: 20, color: '#7C3AED' },
      { symbol: 'BND', pct: 15, color: '#3B82F6' },
      { symbol: 'VWO', pct: 15, color: '#10B981' },
      { symbol: 'GLD', pct: 10, color: '#F59E0B' },
      { symbol: 'SCHD', pct: 5, color: '#EC4899' },
    ],
  },
  visionary: {
    name: 'Visionario',
    icon: '🚀',
    color: '#E85D4C',
    desc: 'Apuesta por las empresas que estan cambiando el mundo',
    risk: 'Agresivo',
    expectedReturn: '12-18% anual',
    composition: [
      { symbol: 'QQQ', pct: 25, color: '#7C3AED' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'SOXX', pct: 15, color: '#DC2626' },
      { symbol: 'PLTR', pct: 10, color: '#F59E0B' },
      { symbol: 'VWO', pct: 10, color: '#10B981' },
      { symbol: 'ARKK', pct: 10, color: '#EC4899' },
      { symbol: 'HIMS', pct: 5, color: '#8B5CF6' },
      { symbol: 'DUOL', pct: 5, color: '#06B6D4' },
    ],
  },
  income_builder: {
    name: 'Constructor de Ingresos',
    icon: '💰',
    color: '#C9A961',
    desc: 'Genera dividendos crecientes que trabajan para ti 24/7',
    risk: 'Moderado-Conservador',
    expectedReturn: '7-10% anual + 2.8% dividendos',
    composition: [
      { symbol: 'SCHD', pct: 30, color: '#10B981' },
      { symbol: 'VYM', pct: 20, color: '#059669' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'BND', pct: 15, color: '#3B82F6' },
      { symbol: 'O', pct: 10, color: '#F59E0B' },
      { symbol: 'KO', pct: 5, color: '#DC2626' },
    ],
  },
};

function StepDots({ total, current }) {
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`rounded-full transition-all duration-300 ${
            i === current ? 'w-8 h-2 bg-[#2A59C6]' : i < current ? 'w-2 h-2 bg-[#2A59C6]' : 'w-2 h-2 bg-[#E5E7EB]'
          }`}
        />
      ))}
    </div>
  );
}

function CompositionBar({ composition }) {
  return (
    <div className="w-full">
      <div className="flex h-3 rounded-full overflow-hidden mb-3">
        {composition.map((item) => (
          <div
            key={item.symbol}
            style={{ width: `${item.pct}%`, backgroundColor: item.color }}
            className="transition-all"
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {composition.map((item) => (
          <div key={item.symbol} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-xs font-semibold text-[#181D27]">{item.symbol}</span>
            <span className="text-xs text-[#9CA3AF]">{item.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InvestorOnboarding({ onComplete }) {
  // Perfilador por VOZ (V8): el Oraculo conversa las preguntas y asigna el portafolio.
  const [showVoiceProfiler, setShowVoiceProfiler] = useState(false);
  const snap = useSnapshot(state);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [multiSelected, setMultiSelected] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const question = QUESTIONS[step];
  const isMulti = question?.multi;

  const handleSelect = (value) => {
    if (isMulti) {
      if (value === 'all') {
        setMultiSelected(['all']);
      } else {
        const without = multiSelected.filter(v => v !== 'all');
        if (without.includes(value)) {
          setMultiSelected(without.filter(v => v !== value));
        } else {
          setMultiSelected([...without, value]);
        }
      }
    } else {
      setSelected(value);
    }
  };

  const canProceed = isMulti ? multiSelected.length > 0 : selected !== null;

  const handleNext = async () => {
    if (!canProceed) return;

    const value = isMulti ? multiSelected : selected;
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);
    setSelected(null);
    setMultiSelected([]);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Submit to backend
      setLoading(true);
      setError('');
      try {
        const response = await completeOnboarding(newAnswers);

        // Auto-aprovisiona (idempotente) la cuenta Alpaca al terminar el onboarding.
        // En BETA el backend la fondea con saldo virtual de practica (paridad con los
        // demos); en produccion BETA_INITIAL_FUND_USD=0 y el usuario deposita USDC real.
        // No depende de nombre/apellido (el backend los infiere del token/email).
        let alpacaInfo = null;
        try {
          const ensured = await ensureAlpacaAccount();
          if (ensured?.ok && ensured.alpacaAccountId) {
            alpacaInfo = ensured;
            // Persist to localStorage so getAlpacaAccountId() picks it up
            const stored = JSON.parse(localStorage.getItem('fp_investor_profile') || '{}');
            localStorage.setItem('fp_investor_profile', JSON.stringify({
              ...stored,
              ...response,
              alpacaAccountId: ensured.alpacaAccountId,
            }));
          } else {
            console.warn('Alpaca ensure sin cuenta (no bloqueante):', ensured?.code, ensured?.message);
          }
        } catch (alpacaErr) {
          console.warn('Alpaca ensure fallo (no bloqueante):', alpacaErr.message);
        }

        setResult({ ...response, alpaca: alpacaInfo });
        setShowResult(true);
      } catch (err) {
        // Fallback: calculate locally if backend is offline
        console.warn('Backend offline, calculating locally:', err.message);
        const portfolio = calculateLocalPortfolio(newAnswers);
        setResult({
          portfolio,
          thesis: PORTFOLIOS[portfolio]?.desc || '',
          tier: 'basic',
          level: 1,
        });
        setShowResult(true);
        // Save locally as backup
        localStorage.setItem('fp_investor_profile', JSON.stringify({
          answers: newAnswers,
          portfolio,
          createdAt: new Date().toISOString(),
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      const prevQuestion = QUESTIONS[step - 1];
      setStep(step - 1);
      if (prevQuestion.multi) {
        setMultiSelected(answers[prevQuestion.id] || []);
        setSelected(null);
      } else {
        setSelected(answers[prevQuestion.id] || null);
        setMultiSelected([]);
      }
    }
  };

  const handleConfirm = () => {
    onComplete(result);
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="animate-spin w-10 h-10 border-3 border-[#2A59C6] border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-[#6B7280]">Analizando tu perfil...</p>
      </div>
    );
  }

  // Result screen
  if (showResult && result) {
    const portfolio = PORTFOLIOS[result.portfolio];
    if (!portfolio) {
      return (
        <div className="max-w-lg mx-auto px-4 py-10 text-center">
          <p className="text-sm text-red-500">Error: portafolio no encontrado</p>
          <button onClick={() => { setShowResult(false); setStep(0); setAnswers({}); }} className="mt-4 text-sm text-[#2A59C6]">Reintentar</button>
        </div>
      );
    }

    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header — el NOMBRE viene del backend ('Explorer Puente'), no de la
            lista local de cuatro. Sin esto el cliente respondia la pregunta de
            la moneda, el backend le asignaba una mezcla 40/60... y la pantalla
            le mostraba "Explorador" con la composicion vieja, 100% dolares. */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <PortfolioMark portfolio={result.portfolio} size={56} />
          </div>
          <h2 className="text-2xl font-bold text-[#181D27] mb-1">
            Tu portafolio: {result.nombre || portfolio.name}
          </h2>
          <p className="text-sm text-[#6B7280]">{portfolio.desc}</p>
        </div>

        {/* LA MEZCLA DE MONEDAS. Es lo nuevo y lo que el cliente no sabe: que
            su plata no queda toda en dolares, y por que. */}
        {result.exposicion && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-4 mb-4">
            <div className="text-xs font-semibold text-[#181D27] mb-3">Cómo queda repartida tu plata</div>
            <div className="flex h-3 rounded-full overflow-hidden mb-3">
              <div style={{ width: `${result.exposicion.cop}%`, background: '#F59E0B' }} />
              <div style={{ width: `${result.exposicion.usd}%`, background: '#2A59C6' }} />
            </div>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-[#B45309] font-semibold">{result.exposicion.cop}% en pesos</span>
              <span className="text-[#2A59C6] font-semibold">{result.exposicion.usd}% en dólares</span>
            </div>
            <p className="text-xs text-[#6B7280] leading-relaxed">{result.exposicion.porQue}</p>
            {result.exposicion.cop > 0 && (
              <p className="mt-2 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#F4D68C] rounded-lg px-3 py-2">
                La parte en pesos son fondos colombianos de Progresión. Estamos terminando
                la conexión: muy pronto los vas a poder comprar desde aquí. Hoy puedes
                invertir la parte en dólares.
              </p>
            )}
          </div>
        )}

        {/* Thesis */}
        <div className="bg-[#F0F4FF] rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <span className="text-lg">🔮</span>
            <div>
              <div className="text-xs font-semibold text-[#2A59C6] mb-1">Tu tesis de inversión (según El Oráculo)</div>
              <p className="text-sm text-[#4B5563] leading-relaxed">{result.thesis}</p>
            </div>
          </div>
        </div>

        {/* Alpaca account status */}
        {result.alpaca?.alpacaAccountId && (
          <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-2xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-lg">🎉</span>
              <div>
                <div className="text-xs font-semibold text-[#059669] mb-1">Cuenta de inversión activada</div>
                <p className="text-sm text-[#14532D] leading-relaxed">
                  Tu cuenta de broker ya está lista. Para empezar a invertir,
                  pasa dólares desde tu billetera FlowexPay a tu cuenta de inversión —
                  en menos de un minuto verás tu saldo disponible.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-3 text-center">
            <div className="text-xs text-[#9CA3AF] mb-1">Riesgo</div>
            <div className="text-sm font-semibold" style={{ color: portfolio.color }}>{portfolio.risk}</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-3 text-center">
            <div className="text-xs text-[#9CA3AF] mb-1">Retorno esperado</div>
            <div className="text-sm font-semibold text-[#181D27]">{portfolio.expectedReturn}</div>
          </div>
        </div>

        {/* Composition */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="text-sm font-semibold text-[#181D27] mb-4">Composición sugerida</div>

          {/* LAS DOS PATAS. Si el backend manda `posiciones` (portafolio
              multimoneda), se pintan separadas: los fondos colombianos primero
              —que es lo nuevo y lo que el cliente no espera— y despues los ETFs.
              Sin esto la pantalla mostraba solo la parte en dolares y el cliente
              se quedaba sin saber que existe la otra mitad de su portafolio.
              Si no vienen (perfil viejo), se usa la composicion de siempre. */}
          {Array.isArray(result.posiciones) && result.posiciones.length ? (
            <div className="space-y-4">
              {['COP', 'USD'].map((mon) => {
                const items = result.posiciones.filter((x) => x.moneda === mon);
                if (!items.length) return null;
                const enVitrina = mon === 'COP';
                return (
                  <div key={mon}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#6B7280]">
                        {enVitrina ? 'En pesos · fondos de Progresión' : 'En dólares · ETFs y acciones'}
                      </span>
                      <span className="text-xs font-bold text-[#181D27]">
                        {items.reduce((a, x) => a + x.pct, 0).toFixed(0)}%
                      </span>
                    </div>
                    <div className="space-y-1">
                      {items.map((x) => {
                        // QUE ES cada cosa, no solo su nombre y su porcentaje.
                        // Pedirle a alguien que ponga plata en "Rentar II" o en
                        // "VWO" sin decirle que son no es informar: es suponer
                        // que ya sabe. La pantalla de Mi Plan ya lo hacia con
                        // los ETFs; aqui faltaba, y los fondos colombianos no lo
                        // tenian en ninguna parte.
                        const info = getPosicionInfo(x);
                        return (
                          <div key={x.ticker || x.fondo} className="flex items-start justify-between gap-3 py-1">
                            <div className="min-w-0">
                              <span className={`text-sm font-medium ${enVitrina ? 'text-[#6B7280]' : 'text-[#181D27]'}`}>
                                {x.ticker || x.fondo}
                              </span>
                              {info?.name && (
                                <span className="text-xs text-[#9CA3AF]"> · {info.name}</span>
                              )}
                              {info?.what && (
                                <div className="text-xs text-[#9CA3AF] leading-snug">{info.what}</div>
                              )}
                            </div>
                            <span className={`text-sm shrink-0 ${enVitrina ? 'text-[#6B7280]' : 'text-[#181D27] font-medium'}`}>
                              {x.pct}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {enVitrina && (
                      <p className="mt-2 text-[11px] text-[#B54708]">
                        Estamos terminando la conexión con Progresión: muy pronto los compras desde aquí.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <CompositionBar composition={portfolio.composition} />
          )}
        </div>

        {/* Level badge */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F0F4FF] flex items-center justify-center text-lg">🔍</div>
            <div>
              <div className="text-sm font-semibold text-[#181D27]">Nivel 1: Curioso</div>
              <div className="text-xs text-[#6B7280]">Desbloquea más análisis invirtiendo y aprendiendo</div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F9FAFB] rounded-xl p-3 mb-6">
          <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
            Rendimientos pasados no garantizan resultados futuros. Este portafolio es una sugerencia basada en tu perfil. Puedes personalizarlo en cualquier momento.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => { setShowResult(false); setStep(0); setAnswers({}); setSelected(null); setMultiSelected([]); }}
            className="flex-1 py-3.5 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB] transition"
          >
            Repetir
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition"
          >
            Comenzar
          </button>
        </div>
      </div>
    );
  }

  // Question flow
  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className={`text-sm text-[#6B7280] hover:text-[#2A59C6] transition ${step === 0 ? 'invisible' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <StepDots total={QUESTIONS.length} current={step} />
        <span className="text-xs text-[#9CA3AF]">{step + 1}/{QUESTIONS.length}</span>
      </div>

      {/* Question */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-[#2A59C6] uppercase tracking-wider mb-2">{question.title}</div>
        <h2 className="text-xl font-bold text-[#181D27]">{question.question}</h2>
        {isMulti && <p className="text-xs text-[#9CA3AF] mt-1">Puedes seleccionar varios</p>}
      </div>

      {/* Options */}
      <div className="space-y-3 mb-8">
        {question.options.map((opt) => {
          const isSelected = isMulti ? multiSelected.includes(opt.value) : selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-[#2A59C6] bg-[#F0F4FF]'
                  : 'border-[#E5E7EB] bg-white hover:border-[#C7D2E8]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-sm font-semibold ${isSelected ? 'text-[#2A59C6]' : 'text-[#181D27]'}`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{opt.desc}</div>
                </div>
                <div className={`w-5 h-5 rounded-${isMulti ? 'md' : 'full'} border-2 flex items-center justify-center transition ${
                  isSelected ? 'border-[#2A59C6] bg-[#2A59C6]' : 'border-[#D1D5DB]'
                }`}>
                  {isSelected && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && <p className="text-xs text-red-500 mb-3 text-center">{error}</p>}

      {/* Next button */}
      <button
        onClick={handleNext}
        disabled={!canProceed}
        className={`w-full py-3.5 rounded-xl text-sm font-semibold transition ${
          canProceed
            ? 'bg-[#2A59C6] text-white hover:bg-[#1E4BB8]'
            : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
        }`}
      >
        {step < QUESTIONS.length - 1 ? 'Siguiente' : 'Ver mi portafolio'}
      </button>

      {/* Perfilador por VOZ: el Oraculo conversa las preguntas (V8) */}
      {step === 0 && (
        <button
          onClick={() => setShowVoiceProfiler(true)}
          className="w-full mt-3 py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 text-white"
          style={{ background: 'linear-gradient(135deg, #0A2540 0%, #2A59C6 100%)' }}
        >
          🎙️ Prefiero hacerlo hablando con el Oraculo
          <span className="text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#C9A961', color: '#0A2540' }}>Nuevo</span>
        </button>
      )}
      {showVoiceProfiler && (
        <OraculoVoice
          phone={null}
          onClose={() => {
            setShowVoiceProfiler(false);
            // Si el perfil quedo creado por voz, recargar deja ver el plan asignado.
            window.location.reload();
          }}
        />
      )}

      {/* Skip */}
      {step === 0 && (
        <button
          onClick={() => onComplete(null)}
          className="w-full mt-3 py-2 text-xs text-[#9CA3AF] hover:text-[#6B7280] transition"
        >
          Omitir por ahora
        </button>
      )}
    </div>
  );
}

// Local fallback when backend is offline
function calculateLocalPortfolio(answers) {
  if (answers.reactionToDrop === 'sell' || (answers.timeHorizon === 'short' && answers.excitement === 'safe_growth')) {
    return 'guardian';
  }
  if (answers.excitement === 'passive_income') {
    return 'income_builder';
  }
  if (answers.reactionToDrop === 'buy_more' && (answers.timeHorizon === 'long' || answers.timeHorizon === 'unlimited') && answers.excitement === 'world_changers') {
    return 'visionary';
  }
  return 'explorer';
}

export { PORTFOLIOS };
